import { transition } from '../domain/outbox-item.js';
import type { DexieOutboxRepository } from '../infrastructure/dexie-outbox.repository.js';

/**
 * ResolveRejectedAction — the three operator actions RejectedItemCard
 * (design §5, `/pending`'s conflict UX) offers per rejection reason:
 * - `force` — duplicate/conflict codes (duplicate_active_ticket,
 *   idempotency_key_reused, qr_tag_conflict): the operator confirms they
 *   want to submit anyway. Sets `payload.force = true` — the SAME flag
 *   CheckinPage's own duplicate-plate dialog already sends on a
 *   confirmed force-submit (see submitCreate in CheckinPage.tsx) — before
 *   moving the item back to `pending` for the drainer to retry.
 * - `edit` — validation-style rejections (422): merges a corrected field
 *   patch into `payload` (e.g. a fixed plate) before retrying. A full
 *   multi-field form editor reusing CheckinPage's own capture flow is
 *   OUT of this slice's scope (would need prefill/edit-mode wiring across
 *   CheckinPage) — RejectedItemCard exposes a minimal, targeted edit
 *   surface instead (see its own component for which fields).
 * - `discard` — the operator gives up on this item permanently
 *   (`discarded` is terminal; its blobs become eligible for cleanup via
 *   `canCleanupBlobsFor`).
 */
export type ResolveRejectedAction =
  | { type: 'force' }
  | { type: 'edit'; patch: Record<string, unknown> }
  | { type: 'discard' };

/**
 * resolveRejected — design §5's `resolveRejected (edit/force/discard)`.
 * Reads the CURRENT row (never a stale snapshot the caller might be
 * holding), applies the action, and persists via the SAME `transition()`
 * state machine the drainer itself uses — a rejected→pending/discarded
 * event fired against a NON-rejected item throws
 * `InvalidOutboxTransitionError` (the item raced to a different state
 * since the operator last saw it), surfaced to the caller rather than
 * silently no-op'd.
 */
export async function resolveRejected(
  repo: Pick<DexieOutboxRepository, 'get' | 'put'>,
  itemId: string,
  action: ResolveRejectedAction,
) {
  const item = await repo.get(itemId);
  if (!item) {
    throw new Error(`resolveRejected: no outbox item with id "${itemId}"`);
  }

  if (action.type === 'discard') {
    const next = transition(item, { type: 'discard' });
    await repo.put(next);
    return next;
  }

  if (action.type === 'force') {
    const withForce = { ...item, payload: { ...item.payload, force: true } };
    const next = transition(withForce, { type: 'force' });
    await repo.put(next);
    return next;
  }

  const patched = { ...item, payload: { ...item.payload, ...action.patch } };
  const next = transition(patched, { type: 'edit' });
  await repo.put(next);
  return next;
}
